import React, { useState, useEffect, useMemo } from 'react';
import { X, ShieldCheck, User, Phone, Building, DollarSign, Lock, CheckCircle, AlertTriangle, Trash2, Upload, File, FileText, Link as LinkIcon, Eye, Clock, Mail, Plus, Minus, History } from 'lucide-react';
import { useToast } from './Toast';
import { api } from '../services/api';
import FilePreviewModal from './FilePreviewModal';

const TaskModal = ({ task, allTasks, projectFiles, contractors = [], onClose, onSave, onDelete, onUploadFile, onDeleteFile }) => {
  const { showToast } = useToast();
  
  const getTodayString = () => new Date().toLocaleDateString('en-CA');

  const [formData, setFormData] = useState({
    name: '', category: 'General', 
    startDate: getTodayString(), duration: 1,
    budgetEstimated: 0, budgetActual: 0, progress: 0,
    contractor: '', contact: { name: '', phone: '', email: '' },
    dependencies: [], materials: [], inspections: [], 
    isInspection: false, notes: '' 
  });
  
  const [newNote, setNewNote] = useState('');
  const [noteLog, setNoteLog] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [existingCategories, setExistingCategories] = useState([]);
  const [newInsp, setNewInsp] = useState({ date: getTodayString(), status: 'Pass', note: '' });
  
  const [showFileLinker, setShowFileLinker] = useState(false);
  const [fileToLink, setFileToLink] = useState('');
  const [previewFile, setPreviewFile] = useState(null);

  // Budget +/- adjustment popup state
  const [adjustType, setAdjustType] = useState(null); // 'add' | 'subtract' | null
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const cats = [...new Set(allTasks.map(t => t.category || 'General'))];
    setExistingCategories(['Site Work', 'Foundation', 'Framing', 'Plumbing', 'Electrical', 'HVAC', 'Finishing', ...cats]);

    if (task) {
        setFormData({
            ...task,
            startDate: task.startDate || getTodayString(),
            contractor: task.contractor || '',
            contact: task.contact || { name: '', phone: '', email: '' },
            dependencies: task.dependencies || [],
            inspections: task.inspections || [],
            isInspection: !!task.isInspection,
            budgetEstimated: task.budgetEstimated || 0,
            budgetActual: task.budgetActual || 0
        });
        
        if (task.notes) {
             try { const parsed = JSON.parse(task.notes); setNoteLog(Array.isArray(parsed) ? parsed : [{ date: 'Old Note', text: task.notes }]); } 
             catch(e) { setNoteLog([{ date: 'Old Note', text: task.notes }]); }
        }

        if (task.id) {
            setLoadingHistory(true);
            api.getBudgetAdjustments(task.id)
                .then(rows => setAdjustmentHistory(rows || []))
                .catch(() => setAdjustmentHistory([]))
                .finally(() => setLoadingHistory(false));
        }
    }
  }, [task, allTasks]);

  const handleContractorSelect = (e) => {
      const selectedValue = e.target.value;
      const contractor = contractors.find(c => c.name === selectedValue || c.name.toLowerCase() === selectedValue.toLowerCase());
      if (contractor) {
          setFormData(prev => ({ ...prev, contractor: contractor.name, contact: { name: contractor.name, phone: contractor.phone || '', email: contractor.email || '' } }));
      } else {
          setFormData(prev => ({ ...prev, contractor: selectedValue }));
      }
  };

  const handleLinkFile = async () => {
      if(!fileToLink || !task?.id) return;
      try { await api.linkFile(fileToLink, task.id); showToast('File Linked'); setShowFileLinker(false); } catch(e) { showToast('Link Failed', 'error'); }
  };

  const dependencyEndDate = useMemo(() => {
      if (!formData.dependencies || formData.dependencies.length === 0) return null;
      let maxEnd = null;
      formData.dependencies.forEach(depId => {
          const parent = allTasks.find(t => t.id === depId);
          if (parent) {
              const pStart = new Date(parent.startDate + 'T00:00:00');
              pStart.setDate(pStart.getDate() + (parent.duration || 1));
              const pEndStr = pStart.toLocaleDateString('en-CA');
              if (!maxEnd || pEndStr > maxEnd) maxEnd = pEndStr;
          }
      });
      return maxEnd;
  }, [formData.dependencies, allTasks]);

  useEffect(() => { if (dependencyEndDate && formData.startDate !== dependencyEndDate) setFormData(prev => ({ ...prev, startDate: dependencyEndDate })); }, [dependencyEndDate, formData.startDate]);

  const completionDate = useMemo(() => {
      if (!formData.startDate) return '-';
      const start = new Date(formData.startDate + 'T00:00:00');
      start.setDate(start.getDate() + (parseInt(formData.duration) || 1));
      return start.toLocaleDateString('en-CA');
  }, [formData.startDate, formData.duration]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleContactChange = (field, value) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
  const toggleDependency = (id) => setFormData(prev => ({ ...prev, dependencies: prev.dependencies.includes(id) ? prev.dependencies.filter(d => d !== id) : [...prev.dependencies, id] }));
  const handleAddNote = () => { if (!newNote.trim()) return; setNoteLog([{ date: new Date().toLocaleString(), text: newNote }, ...noteLog]); setNewNote(''); };
  const handleAddInspection = () => { setFormData({ ...formData, inspections: [...formData.inspections, newInsp], progress: newInsp.status === 'Pass' ? 100 : newInsp.status === 'Fail' ? 0 : formData.progress }); setNewInsp({ date: getTodayString(), status: 'Pass', note: '' }); };
  const handleDeleteInspection = (idx) => setFormData(prev => ({ ...prev, inspections: prev.inspections.filter((_, i) => i !== idx) }));

  const openAdjust = (type) => {
      if (!task?.id) return showToast('Save task first before adjusting budget', 'error');
      setAdjustType(type);
      setAdjustAmount('');
      setAdjustReason('');
  };

  const handleSubmitAdjustment = async () => {
      const amt = parseFloat(adjustAmount);
      if (!amt || amt <= 0) return showToast('Enter a valid amount', 'error');
      setIsAdjusting(true);
      try {
          const res = await api.adjustTaskBudget(task.id, adjustType, amt, adjustReason.trim());
          setFormData(prev => ({ ...prev, budgetActual: res.budgetActual }));
          setAdjustmentHistory(prev => [{
              id: res.adjustmentId, type: adjustType, amount: amt, reason: adjustReason.trim(),
              resulting_actual: res.budgetActual, created_at: new Date().toISOString()
          }, ...prev]);
          showToast(adjustType === 'add' ? 'Added to actual cost' : 'Subtracted from actual cost');
          setAdjustType(null);
      } catch (e) {
          showToast('Adjustment failed: ' + e.message, 'error');
      } finally {
          setIsAdjusting(false);
      }
  };
  
  const handleSaveWrapper = () => {
      const payload = {
          ...formData,
          budgetEstimated: parseFloat(formData.budgetEstimated) || 0,
          budgetActual: parseFloat(formData.budgetActual) || 0,
          notes: JSON.stringify(noteLog)
      };
      onSave(payload);
  };
  
  const handleDelete = () => { if(window.confirm("Delete this task?")) { const tid = task.id || task.task_id; if (tid) { onDelete(tid); onClose(); } } };
  
  const handleTaskFileUpload = async (e) => { 
      if (!task?.id) return showToast("Save task first.", "error"); 
      const file = e.target.files[0]; if (!file) return; 
      setIsUploading(true); 
      try { await onUploadFile(file, task.id, 'Task Attachment'); showToast('Uploaded'); } catch(err) { showToast('Upload failed', 'error'); } 
      finally { setIsUploading(false); } 
  };
  
  const sortedPotentialDependencies = useMemo(() => [...allTasks].filter(t => t.id !== (task?.id)).sort((a,b) => new Date(a.startDate) - new Date(b.startDate)), [allTasks, task]);

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
            <h3 className="text-xl font-bold flex items-center gap-2 text-gray-800">{formData.isInspection ? <ShieldCheck className="text-purple-600"/> : <FileText className="text-blue-600"/>}{task ? 'Edit Task' : 'New Task'}</h3>
            <button onClick={onClose}><X size={24} className="text-gray-400 hover:text-gray-600"/></button>
        </div>
        <div className="flex border-b bg-gray-50">
            <button onClick={() => setActiveTab('details')} className={`flex-1 py-3 text-sm font-bold ${activeTab==='details' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>Details</button>
            <button onClick={() => setActiveTab('schedule')} className={`flex-1 py-3 text-sm font-bold ${activeTab==='schedule' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>Schedule</button>
            {formData.isInspection && <button onClick={() => setActiveTab('inspections')} className={`flex-1 py-3 text-sm font-bold ${activeTab==='inspections' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>Inspections</button>}
            <button onClick={() => setActiveTab('notes')} className={`flex-1 py-3 text-sm font-bold ${activeTab==='notes' ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-500 hover:bg-gray-100'}`}>Files & Logs</button>
        </div>

        <div className="p-6 space-y-6">
            {activeTab === 'details' && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="text-xs font-bold text-gray-500 uppercase">Task Name</label><input className="w-full border rounded p-2 font-bold text-gray-800" value={formData.name} onChange={e => handleChange('name', e.target.value)} autoFocus /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase">Category</label><input className="w-full border rounded p-2" value={formData.category} onChange={e => handleChange('category', e.target.value)} list="categories"/><datalist id="categories">{[...new Set(existingCategories)].map(c => <option key={c} value={c}/>)}</datalist></div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg border"><div className="flex justify-between items-center mb-2"><label className="text-xs font-bold text-gray-500 uppercase">Completion</label><span className={`text-sm font-bold ${formData.progress === 100 ? 'text-green-600' : 'text-blue-600'}`}>{formData.progress}%</span></div><input type="range" min="0" max="100" className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" value={formData.progress} onChange={e => handleChange('progress', parseInt(e.target.value))} /></div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2"><DollarSign size={16}/> Financials</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-gray-600">Estimated Cost ($)</label>
                                <input type="number" className="w-full border rounded p-2" value={formData.budgetEstimated} onChange={e => handleChange('budgetEstimated', parseFloat(e.target.value)||0)} />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600">Actual Cost ($)</label>
                                <div className="flex items-center gap-1">
                                    <input type="number" className="w-full border rounded p-2" value={formData.budgetActual} onChange={e => handleChange('budgetActual', parseFloat(e.target.value)||0)} />
                                    <button type="button" onClick={() => openAdjust('add')} title="Add to actual cost"
                                        className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-green-600 text-white rounded hover:bg-green-700">
                                        <Plus size={16}/>
                                    </button>
                                    <button type="button" onClick={() => openAdjust('subtract')} title="Subtract from actual cost"
                                        className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-red-500 text-white rounded hover:bg-red-600">
                                        <Minus size={16}/>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Adjustment popup */}
                        {adjustType && (
                            <div className="mt-3 bg-white border-2 border-dashed border-gray-300 rounded-lg p-3">
                                <div className="flex justify-between items-center mb-2">
                                    <span className={`text-sm font-bold flex items-center gap-1 ${adjustType === 'add' ? 'text-green-700' : 'text-red-700'}`}>
                                        {adjustType === 'add' ? <Plus size={14}/> : <Minus size={14}/>}
                                        {adjustType === 'add' ? 'Add to' : 'Subtract from'} Actual Cost
                                    </span>
                                    <button onClick={() => setAdjustType(null)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
                                </div>
                                <div className="flex gap-2 mb-2">
                                    <div className="relative flex-1">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                        <input type="number" autoFocus className="w-full border rounded p-2 pl-6 text-sm" placeholder="0.00"
                                            value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSubmitAdjustment()} />
                                    </div>
                                    <button onClick={handleSubmitAdjustment} disabled={isAdjusting}
                                        className={`px-4 rounded text-white text-sm font-bold disabled:opacity-50 ${adjustType === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}>
                                        {isAdjusting ? '...' : 'Confirm'}
                                    </button>
                                </div>
                                <input className="w-full border rounded p-2 text-sm" placeholder="Reason (e.g. material price increase, change order, refund)"
                                    value={adjustReason} onChange={e => setAdjustReason(e.target.value)} />
                            </div>
                        )}

                        {/* Adjustment history */}
                        {task?.id && (
                            <div className="mt-3">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase mb-1.5">
                                    <History size={12}/> Adjustment History
                                </div>
                                {loadingHistory ? (
                                    <p className="text-xs text-gray-400 italic">Loading...</p>
                                ) : adjustmentHistory.length ? (
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {adjustmentHistory.map((adj, i) => (
                                            <div key={adj.id || i} className="flex justify-between items-center text-xs bg-white rounded px-2 py-1.5 border">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`font-bold ${adj.type === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                                                        {adj.type === 'add' ? '+' : '-'}${parseFloat(adj.amount).toLocaleString()}
                                                    </span>
                                                    {adj.reason && <span className="text-gray-500">— {adj.reason}</span>}
                                                </div>
                                                <span className="text-gray-400 flex-shrink-0 ml-2">
                                                    {adj.created_at ? new Date(adj.created_at).toLocaleDateString() : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-400 italic">No adjustments logged yet.</p>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Contractor</label>
                            <input className="w-full border rounded p-2" placeholder="Select or Type..." list="contractorsList" value={formData.contractor} onChange={handleContractorSelect} />
                            <datalist id="contractorsList">{contractors.map(c => <option key={c.id} value={c.name}>{c.trade}</option>)}</datalist>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Contact Info</label>
                            <div className="flex gap-2">
                                <input className="flex-1 border rounded p-2" placeholder="Phone" value={formData.contact.phone} onChange={e => handleContactChange('phone', e.target.value)} />
                                <input className="flex-1 border rounded p-2" placeholder="Email" value={formData.contact.email} onChange={e => handleContactChange('email', e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2"><input type="checkbox" id="isInspection" checked={formData.isInspection} onChange={e => handleChange('isInspection', e.target.checked)} className="h-4 w-4 text-purple-600"/><label htmlFor="isInspection" className="text-sm font-bold text-gray-600">Mark as Inspection</label></div>
                </>
            )}

            {activeTab === 'schedule' && (
                <>
                    <div className="grid grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <div><label className="text-xs font-bold text-blue-800 uppercase flex items-center gap-1">Start Date {dependencyEndDate && <Lock size={10}/>}</label><input type="date" className={`w-full border rounded p-2 ${dependencyEndDate ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white'}`} value={formData.startDate} onChange={e => !dependencyEndDate && handleChange('startDate', e.target.value)} disabled={!!dependencyEndDate}/>{dependencyEndDate && <div className="text-[10px] text-blue-600 mt-1">Locked by dependency</div>}</div>
                        <div><label className="text-xs font-bold text-blue-800 uppercase">Duration (Days)</label><input type="number" className="w-full border rounded p-2" value={formData.duration} onChange={e => handleChange('duration', parseInt(e.target.value)||0)} /></div>
                        <div><label className="text-xs font-bold text-blue-800 uppercase">Completion</label><div className="w-full border rounded p-2 bg-white text-gray-700 font-medium">{completionDate}</div></div>
                    </div>
                    <div><div className="flex justify-between items-center mb-2"><h4 className="font-bold text-gray-700">Dependencies</h4><span className="text-xs text-gray-400">Predecessors</span></div><div className="flex flex-col gap-1 max-h-60 overflow-y-auto border rounded-lg p-2 bg-gray-50">{sortedPotentialDependencies.map(t => (<label key={t.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${formData.dependencies.includes(t.id) ? 'bg-blue-100 border border-blue-200' : 'hover:bg-white'}`}><input type="checkbox" checked={formData.dependencies.includes(t.id)} onChange={() => toggleDependency(t.id)} className="h-4 w-4 text-blue-600"/><span className="flex-1 font-medium text-sm">{t.name}</span><span className="text-xs text-gray-500">{t.startDate}</span></label>))}</div></div>
                </>
            )}

            {activeTab === 'inspections' && (
                <div className="space-y-4">
                    <h4 className="font-bold text-gray-700 flex items-center gap-2"><ShieldCheck size={18} className="text-purple-600"/> Inspection History</h4>
                    <div className="space-y-2">{formData.inspections.map((insp, i) => (<div key={i} className={`flex justify-between items-center p-3 rounded border ${insp.status === 'Pass' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}><div><div className="flex items-center gap-2 font-bold">{insp.status === 'Pass' ? <CheckCircle size={16} className="text-green-600"/> : <AlertTriangle size={16} className="text-red-600"/>}{insp.status} <span className="text-gray-500 text-xs font-normal">on {insp.date}</span></div><div className="text-xs text-gray-600">{insp.note}</div></div><button onClick={() => handleDeleteInspection(i)} className="text-gray-400 hover:text-red-500"><Trash2 size={16}/></button></div>))}</div>
                    <div className="border-t pt-4 mt-4"><h5 className="text-sm font-bold mb-2">Log New Result</h5><div className="grid grid-cols-3 gap-2 mb-2"><input type="date" className="border rounded p-2 text-sm" value={newInsp.date} onChange={e=>setNewInsp({...newInsp, date: e.target.value})} /><select className="border rounded p-2 text-sm" value={newInsp.status} onChange={e=>setNewInsp({...newInsp, status: e.target.value})}><option>Pass</option><option>Fail</option></select><button onClick={handleAddInspection} className="bg-purple-600 text-white rounded font-bold text-sm hover:bg-purple-700">Add Log</button></div><input className="w-full border rounded p-2 text-sm" placeholder="Notes (e.g. Failed due to venting)" value={newInsp.note} onChange={e=>setNewInsp({...newInsp, note: e.target.value})} /></div>
                </div>
            )}

            {activeTab === 'notes' && (
                <div className="h-full flex flex-col gap-4">
                    <div className="flex-1 flex flex-col gap-2">
                        <h4 className="font-bold text-sm">Work Logs</h4>
                        <div className="flex gap-2"><input className="flex-1 border rounded p-2 text-sm" placeholder="Add a log entry..." value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddNote()} /><button onClick={handleAddNote} className="bg-gray-800 text-white px-3 py-2 rounded text-xs font-bold">Add Log</button></div>
                        <div className="bg-gray-50 border rounded-lg p-3 h-48 overflow-y-auto space-y-3">{noteLog.map((log, i) => (<div key={i} className="bg-white p-2 rounded border shadow-sm text-sm"><div className="text-[10px] text-blue-600 font-bold mb-1 flex items-center gap-1"><Clock size={10}/> {log.date}</div><div className="text-gray-700">{log.text}</div></div>))}</div>
                    </div>
                    <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="font-bold text-sm">Attached Files</h4>
                            <div className="flex gap-2">
                                <button onClick={() => setShowFileLinker(!showFileLinker)} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded border hover:bg-gray-200 flex items-center gap-1"><LinkIcon size={12}/> Link Existing</button>
                                <label className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded cursor-pointer border border-blue-100 hover:bg-blue-100">{isUploading ? '...' : '+ Upload'}<input type="file" className="hidden" onChange={handleTaskFileUpload} disabled={isUploading}/></label>
                            </div>
                        </div>
                        {showFileLinker && (
                            <div className="bg-orange-50 p-2 rounded mb-2 border border-orange-200 text-xs">
                                <p className="mb-1 font-bold text-orange-800">Link Existing File</p>
                                <select className="w-full border rounded p-1 mb-2" onChange={e => setFileToLink(e.target.value)}>
                                    <option value="">Select a file...</option>
                                    {projectFiles.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                                <button onClick={handleLinkFile} className="bg-orange-500 text-white px-3 py-1 rounded w-full">Attach</button>
                            </div>
                        )}
                        <div className="space-y-1">{projectFiles.filter(f => f.taskId === task?.id).map(f => (
                            <div key={f.id} className="flex justify-between items-center p-2 border rounded text-xs bg-gray-50">
                                <div className="flex items-center gap-2 truncate cursor-pointer hover:text-blue-600" onClick={() => setPreviewFile(f)}>
                                    <Eye size={12} className="text-gray-400"/>
                                    <span className="truncate max-w-[150px]">{f.name}</span>
                                </div>
                                <button onClick={() => onDeleteFile(f.id)} className="text-red-500"><Trash2 size={12}/></button>
                            </div>
                        ))}</div>
                    </div>
                </div>
            )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-between sticky bottom-0 rounded-b-xl">
            {task && task.id ? <button onClick={handleDelete} className="text-red-500 hover:text-red-700 flex items-center gap-1 font-bold text-sm"><Trash2 size={16}/> Delete</button> : <div></div>}
            <div className="flex gap-2 ml-auto">
                <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded text-sm font-bold">Cancel</button>
                <button onClick={handleSaveWrapper} className="px-6 py-2 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700 shadow-sm">Save Changes</button>
            </div>
        </div>
      </div>
      {/* PREVIEW MODAL ON TOP */}
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
    </>
  );
};
export default TaskModal;